/*
 * SPDX-FileCopyrightText: 2023 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import {
  ConverterService,
  OccEndpointsService,
  PRODUCT_NORMALIZER,
  Product,
  ProductScope,
  ProductService,
  RoutingService,
  UserIdService,
  isNotUndefined,
} from '@spartacus/core';
import { CurrentProductService } from '@spartacus/storefront';
import { Observable, combineLatest, of } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class UdxCurrentProductService extends CurrentProductService {
  constructor(
    protected customRouting: RoutingService,
    protected customService: ProductService,
    protected http: HttpClient,
    protected occEndpoints: OccEndpointsService,
    protected converter: ConverterService,
    private userService: UserIdService,
    private route: ActivatedRoute
  ) {
    super(customRouting, customService);
  }

  /**
   * Returns an observable for the current product
   * @returns Product
   * @returns null if product can't be found
   *
   * @param scopes
   */
  override getProduct(scopes?: (ProductScope | string)[] | ProductScope | string): Observable<Product | null> {
    return this.getCode().pipe(
      distinctUntilChanged(),
      switchMap((productCode: string) => {
        return productCode ? this.customService.get(productCode, scopes || this.DEFAULT_PRODUCT_SCOPE) : of(null);
      }),
      filter(isNotUndefined)
    );
  }

  protected override getCode(): Observable<string> {
    return this.customRouting.getRouterState().pipe(map((state) => state.state.params['productCode']));
  }

  getGroupProduct() {
    const queryParam = this.getRouteParams();
    return combineLatest([this.getCode(), this.userService.getUserId(), queryParam]).pipe(
      take(1),
      switchMap(([productCode, user, queryParam]) => {
        return productCode ? this.load(productCode, user, queryParam, this.DEFAULT_PRODUCT_SCOPE) : of(null);
      }),
      filter(isNotUndefined)
    );
  }

  getRouteParams(): Observable<Params> {
    return this.route.queryParams;
  }

  load(productCode: string, userId: string, queryParam: Params, scope?: string): Observable<Product> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    let url = this.getEndpoint(productCode, userId, queryParam, scope);
    return this.http.get(url, { headers }).pipe(this.converter.pipeable(PRODUCT_NORMALIZER));
  }

  protected getEndpoint(code: string, userId: string, queryParam: Params, scope?: string): string {
    return this.occEndpoints.buildUrl('groupProduct', {
      urlParams: { userId: userId, groupRuleId: code },
      queryParams: { clear: queryParam['clear'], orderId: queryParam['orderId'] },
      scope,
    });
  }
}
